import {
  AccessTime,
  Add,
  ArrowRight,
  Chat,
  Close,
  HeadsetMic,
  InfoOutlined,
  KeyboardArrowRight,
  Mail,
  MailOutline,
  SettingsInputComponent,
} from "@mui/icons-material";
import React, { useCallback, useMemo, useState } from "react";
import AudioPlayerForRouting from "../reusables/AudioPlayerForRouting";
import ConfigureCallActionModal from "../modals/ConfigureCallActionModal";
import TimePicker from "react-time-picker";
import { Info, MailIcon, ArrowRight as LucideArrowRight } from "lucide-react";
import { Divider } from "@mui/material";
import ConfigureCallActionForBusinessModal from "../modals/ConfigureCallActionForBusinessHours";
import ConfigureNotificationForBusinessHours from "../modals/ConfigureNotificationForBusinessHours";
import ConfigureNotificationForAfterHours from "../modals/ConfigureNotificationForAfterHours";
import ConfigureCallActionForAfterModal from "../modals/ConfigureCallActionForAfterHours";
import ConfigureNotificationModal from "../modals/ConfigureNotificationMainModal";
import TooltipComponent from "../reusables/TooltipComponent";
import { formatPhoneNumber } from "../utils/formatnumber";
import { IVR_MENU_DIGIT_ORDER, resolveOptionDigit } from "./routePayloadNormalize";
import { 
  useForwardAction, 
  DYNAMIC_FORWARD_BACKUP_NONE,
  useSendToVoicemailAction, 
  usePlayRecordingAction, 
  useElevenLabsAction,
  useElevenLabsAgentOptions,
  useTwilanswerAppAction,
  CALL_MENU_CONTAINER_CLASS,
} from "../../hooks/useCallAction";
import ForwardUI from "./ForwardUI";
import TwilanswerAppUI from "./TwilanswerAppUI";
import SendToVoicemailUI from "./SendToVoicemailUI";
import PlayRecordingUI from "./PlayRecordingUI";
import ElevenLabsUI from "./ElevenLabsUI";
import { useNotificationAction } from "../../hooks/useNotificationAction";
import EmailNotificationUI from "./EmailNotificationUI";
import SlackNotificationUI from "./SlackNotificationUI";
import SmsNotificationUI from "./SmsNotificationUI";
import FrontNotificationUI from "./FrontNotificationUI";
import { useRouteConfigModals } from "../contexts/RouteConfigModalsContext";
import { isForwardFamilyAction } from "../../utils/forwardActionMode";
import { resolveForwardNumberPhone } from "../../utils/forwardNumberChannels";
import {
  isTwilanswerAppAction,
  listOrgUsersForDeviceForward,
  numberFieldsForActionChange,
} from "../../utils/forwardDeviceDestinations";
import { useOrgUsers } from "../../hooks/useOrgUsers";

/** Map over ivrMenu.options whether they're stored as an object or array; always returns an object. */
function mapIvrOptions(options, fn) {
  if (!options || typeof options !== "object") return options;
  const entries = Array.isArray(options)
    ? options.map((opt, i) => [String(i), opt])
    : Object.entries(options);
  return Object.fromEntries(entries.map(([k, opt]) => [k, fn(opt, k)]));
}

// Unified Forward wrappers (Simple / Dynamic / VM toggle)
const ForwardIVRWrapper = ({ item, route, setRoute, channels, recordings, setOpenConfigureSlotModal, routeModals, optionKey }) => {
  const forwardLogic = useForwardAction("ivrMenu", route, setRoute, item?.id ?? null, optionKey);
  const handleDestinationChange = useCallback(
    (value, index, currentList) => {
      if (value === DYNAMIC_FORWARD_BACKUP_NONE) {
        forwardLogic.handleNumberChange(DYNAMIC_FORWARD_BACKUP_NONE);
        return;
      }
      const phone = resolveForwardNumberPhone(value, channels);
      if (typeof index === "number" && Array.isArray(currentList)) {
        const next = [...currentList];
        if (index < next.length) next[index] = phone;
        else next.push(phone);
        forwardLogic.handleDestinationsChange(next);
        return;
      }
      forwardLogic.handleNumberChange(phone);
    },
    [channels, forwardLogic.handleNumberChange, forwardLogic.handleDestinationsChange]
  );
  const getAddDestinationOption = useCallback(
    (index) => {
      if (!routeModals) return undefined;
      return {
        label: "Add Forwarding Number",
        onClick: () =>
          routeModals.openAddChannelModal("forward_number", (value) => {
            handleDestinationChange(value, index, forwardLogic.destinationValues || []);
          }),
      };
    },
    [routeModals, forwardLogic.destinationValues, handleDestinationChange]
  );
  const addDestinationOption = getAddDestinationOption(0);
  const addRecordingOption = routeModals
    ? { label: "Add Recording", onClick: () => routeModals.openAddRecordingModal(forwardLogic.handleRecordingChange) }
    : undefined;
  return (
    <ForwardUI
      {...forwardLogic}
      channels={channels}
      recordings={recordings}
      setOpenConfigureSlotModal={setOpenConfigureSlotModal}
      callerIdOptions={forwardLogic.callerIdOptions}
      containerClass={CALL_MENU_CONTAINER_CLASS}
      addDestinationOption={addDestinationOption}
      getAddDestinationOption={getAddDestinationOption}
      addRecordingOption={addRecordingOption}
      onDestinationChange={handleDestinationChange}
    />
  );
};

const TwilanswerAppIVRWrapper = ({
  item,
  route,
  setRoute,
  recordings,
  setOpenConfigureSlotModal,
  routeModals,
  optionKey,
}) => {
  const appLogic = useTwilanswerAppAction(
    "ivrMenu",
    route,
    setRoute,
    item?.id ?? null,
    optionKey
  );
  const [localOrg] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("organization"));
    } catch {
      return null;
    }
  });
  const orgId = localOrg?.orgId?.S ?? localOrg?.orgId ?? null;
  const { users: apiUsers } = useOrgUsers(orgId);
  const users = useMemo(
    () => listOrgUsersForDeviceForward(localOrg, apiUsers),
    [localOrg, apiUsers]
  );
  const addRecordingOption = routeModals
    ? {
        label: "Add Recording",
        onClick: () =>
          routeModals.openAddRecordingModal(appLogic.handleRecordingChange),
      }
    : undefined;
  return (
    <TwilanswerAppUI
      {...appLogic}
      recordings={recordings}
      users={users}
      setOpenConfigureSlotModal={setOpenConfigureSlotModal}
      containerClass={CALL_MENU_CONTAINER_CLASS}
      addRecordingOption={addRecordingOption}
    />
  );
};

const TwilanswerAppIVRWrapperOffHours = ({
  item,
  route,
  setRoute,
  recordings,
  setOpenConfigureSlotModal,
  routeModals,
  optionKey,
}) => {
  const appLogic = useTwilanswerAppAction(
    "ivrMenuOffHours",
    route,
    setRoute,
    item?.id ?? null,
    optionKey
  );
  const [localOrg] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("organization"));
    } catch {
      return null;
    }
  });
  const orgId = localOrg?.orgId?.S ?? localOrg?.orgId ?? null;
  const { users: apiUsers } = useOrgUsers(orgId);
  const users = useMemo(
    () => listOrgUsersForDeviceForward(localOrg, apiUsers),
    [localOrg, apiUsers]
  );
  const addRecordingOption = routeModals
    ? {
        label: "Add Recording",
        onClick: () =>
          routeModals.openAddRecordingModal(appLogic.handleRecordingChange),
      }
    : undefined;
  return (
    <TwilanswerAppUI
      {...appLogic}
      recordings={recordings}
      users={users}
      setOpenConfigureSlotModal={setOpenConfigureSlotModal}
      containerClass={CALL_MENU_CONTAINER_CLASS}
      addRecordingOption={addRecordingOption}
    />
  );
};

const ForwardIVRWrapperOffHours = ({ item, route, setRoute, channels, recordings, setOpenConfigureSlotModal, routeModals, optionKey }) => {
  const forwardLogic = useForwardAction("ivrMenuOffHours", route, setRoute, item?.id ?? null, optionKey);
  const handleDestinationChange = useCallback(
    (value, index, currentList) => {
      if (value === DYNAMIC_FORWARD_BACKUP_NONE) {
        forwardLogic.handleNumberChange(DYNAMIC_FORWARD_BACKUP_NONE);
        return;
      }
      const phone = resolveForwardNumberPhone(value, channels);
      if (typeof index === "number" && Array.isArray(currentList)) {
        const next = [...currentList];
        if (index < next.length) next[index] = phone;
        else next.push(phone);
        forwardLogic.handleDestinationsChange(next);
        return;
      }
      forwardLogic.handleNumberChange(phone);
    },
    [channels, forwardLogic.handleNumberChange, forwardLogic.handleDestinationsChange]
  );
  const getAddDestinationOption = useCallback(
    (index) => {
      if (!routeModals) return undefined;
      return {
        label: "Add Forwarding Number",
        onClick: () =>
          routeModals.openAddChannelModal("forward_number", (value) => {
            handleDestinationChange(value, index, forwardLogic.destinationValues || []);
          }),
      };
    },
    [routeModals, forwardLogic.destinationValues, handleDestinationChange]
  );
  const addDestinationOption = getAddDestinationOption(0);
  const addRecordingOption = routeModals
    ? { label: "Add Recording", onClick: () => routeModals.openAddRecordingModal(forwardLogic.handleRecordingChange) }
    : undefined;
  return (
    <ForwardUI
      {...forwardLogic}
      channels={channels}
      recordings={recordings}
      setOpenConfigureSlotModal={setOpenConfigureSlotModal}
      callerIdOptions={forwardLogic.callerIdOptions}
      containerClass={CALL_MENU_CONTAINER_CLASS}
      addDestinationOption={addDestinationOption}
      getAddDestinationOption={getAddDestinationOption}
      addRecordingOption={addRecordingOption}
      onDestinationChange={handleDestinationChange}
    />
  );
};


// Wrapper component for IVR menu SendToVoicemail action (used in business hours/off-hours sections)
const SendToVoicemailIVRWrapper = ({ item, route, setRoute, recordings, setOpenConfigureCallActionModal, isOffHours = false, routeModals, optionKey }) => {
  const context = "ivrMenu"; // Both use ivrMenu context
  const sendToVoicemailLogic = useSendToVoicemailAction(context, route, setRoute, item?.id ?? null, optionKey);
  const option = optionKey != null ? route?.ivrMenu?.options?.[optionKey] : null;

  // For off-hours section, we need to handle offHoursRecording field
  if (isOffHours) {
    if (optionKey == null) return null; // Guard: must have optionKey to update the correct option
    // Override the recording value and handler for off-hours
    const offHoursRecording = option?.offHoursRecording || "";
    const handleOffHoursRecordingChange = (value) => {
      setRoute((prev) => ({
        ...prev,
        ivrMenu: {
          ...prev.ivrMenu,
          options: {
            ...prev.ivrMenu.options,
            [optionKey]: { ...prev.ivrMenu.options[optionKey], offHoursRecording: value },
          },
        },
      }));
    };
    const addRecordingOption = routeModals
      ? { label: "Add Recording", onClick: () => routeModals.openAddRecordingModal(handleOffHoursRecordingChange) }
      : undefined;
    return (
      <div className="">
        <SendToVoicemailUI
          {...sendToVoicemailLogic}
          recordingValue={offHoursRecording}
          handleRecordingChange={handleOffHoursRecordingChange}
          setOpenConfigureSlotModal={setOpenConfigureCallActionModal}
          recordings={recordings}
          containerClass={CALL_MENU_CONTAINER_CLASS}
          addRecordingOption={addRecordingOption}
        />
      </div>
    );
  }
  
  const addRecordingOption = routeModals
    ? { label: "Add Recording", onClick: () => routeModals.openAddRecordingModal(sendToVoicemailLogic.handleRecordingChange) }
    : undefined;
  return (
    <div className="">
      <SendToVoicemailUI
        {...sendToVoicemailLogic}
        setOpenConfigureSlotModal={setOpenConfigureCallActionModal}
        recordings={recordings}
        containerClass={CALL_MENU_CONTAINER_CLASS}
        addRecordingOption={addRecordingOption}
      />
    </div>
  );
};

// Wrapper component for IVR menu PlayRecording action (used in business hours/off-hours sections)
const PlayRecordingIVRWrapper = ({ item, route, setRoute, recordings, setOpenConfigureCallActionModal, isOffHours = false, routeModals, optionKey }) => {
  const context = isOffHours ? "ivrMenu" : "ivrMenu"; // Both use ivrMenu context
  const playRecordingLogic = usePlayRecordingAction(context, route, setRoute, item?.id ?? null, optionKey);
  const optionForRecording = optionKey != null ? route?.ivrMenu?.options?.[optionKey] : null;

  // For off-hours section, we need to handle offHoursRecording field
  if (isOffHours) {
    if (optionKey == null) return null;
    // Override the recording value and handler for off-hours
    const offHoursRecording = optionForRecording?.offHoursRecording || "";
    const handleOffHoursRecordingChange = (value) => {
      setRoute((prev) => ({
        ...prev,
        ivrMenu: {
          ...prev.ivrMenu,
          options: {
            ...prev.ivrMenu.options,
            [optionKey]: { ...prev.ivrMenu.options[optionKey], offHoursRecording: value },
          },
        },
      }));
    };
    const addRecordingOption = routeModals
      ? { label: "Add Recording", onClick: () => routeModals.openAddRecordingModal(handleOffHoursRecordingChange) }
      : undefined;
    return (
      <div className="">
        <PlayRecordingUI
          {...playRecordingLogic}
          recordingValue={offHoursRecording}
          handleRecordingChange={handleOffHoursRecordingChange}
          setOpenConfigureSlotModal={setOpenConfigureCallActionModal}
          recordings={recordings}
          containerClass={CALL_MENU_CONTAINER_CLASS}
          addRecordingOption={addRecordingOption}
        />
      </div>
    );
  }
  
  const addRecordingOption = routeModals
    ? { label: "Add Recording", onClick: () => routeModals.openAddRecordingModal(playRecordingLogic.handleRecordingChange) }
    : undefined;
  return (
    <div className="">
      <PlayRecordingUI
        {...playRecordingLogic}
        setOpenConfigureSlotModal={setOpenConfigureCallActionModal}
        recordings={recordings}
        containerClass={CALL_MENU_CONTAINER_CLASS}
        addRecordingOption={addRecordingOption}
      />
    </div>
  );
};

// Wrapper component for IVR menu ElevenLabs action
const ElevenLabsIVRWrapper = ({ item, route, setRoute, channels, setOpenConfigureCallActionModal, isOffHours = false, optionKey }) => {
  const context = isOffHours ? "ivrMenuOffHours" : "ivrMenu";
  const elevenLabsLogic = useElevenLabsAction(context, route, setRoute, item?.id ?? null, optionKey);
  const agentOptions = useElevenLabsAgentOptions(channels);

  return (
    <div className="">
      <ElevenLabsUI
        {...elevenLabsLogic}
        setOpenConfigureSlotModal={setOpenConfigureCallActionModal}
        containerClass={CALL_MENU_CONTAINER_CLASS}
        agentOptions={agentOptions}
        hasElevenLabsAgents={agentOptions.length > 0}
      />
    </div>
  );
};

// Wrapper components for IVR menu notifications (business hours)
const EmailNotificationIVRWrapper = ({ item, route, setRoute, channels, setOpenConfigureNotificationModal, isOffHours = false, routeModals, optionKey }) => {
  const notificationLogic = useNotificationAction("email", "ivrMenu", route, setRoute, item?.id ?? null, isOffHours, optionKey);
  const addChannelOption = routeModals
    ? { label: "Add Email Channel", onClick: () => routeModals.openAddChannelModal("email", notificationLogic.handleChannelChange) }
    : undefined;
  return (
    <EmailNotificationUI
      {...notificationLogic}
      setOpenConfigureNotificationModal={setOpenConfigureNotificationModal}
      channels={channels}
      containerClass={CALL_MENU_CONTAINER_CLASS}
      addChannelOption={addChannelOption}
    />
  );
};

const SlackNotificationIVRWrapper = ({ item, route, setRoute, channels, setOpenConfigureNotificationModal, isOffHours = false, routeModals, optionKey }) => {
  const notificationLogic = useNotificationAction("slack", "ivrMenu", route, setRoute, item?.id ?? null, isOffHours, optionKey);
  const addChannelOption = routeModals
    ? { label: "Add Slack Channel", onClick: () => routeModals.openAddChannelModal("slack", notificationLogic.handleChannelChange) }
    : undefined;
  return (
    <SlackNotificationUI
      {...notificationLogic}
      setOpenConfigureNotificationModal={setOpenConfigureNotificationModal}
      channels={channels}
      containerClass={CALL_MENU_CONTAINER_CLASS}
      addChannelOption={addChannelOption}
    />
  );
};

const SmsNotificationIVRWrapper = ({ item, route, setRoute, channels, setOpenConfigureNotificationModal, isOffHours = false, routeModals, optionKey }) => {
  const notificationLogic = useNotificationAction("sms", "ivrMenu", route, setRoute, item?.id ?? null, isOffHours, optionKey);
  const addChannelOption = routeModals
    ? { label: "Add SMS Channel", onClick: () => routeModals.openAddChannelModal("sms", notificationLogic.handleChannelChange) }
    : undefined;
  return (
    <SmsNotificationUI
      {...notificationLogic}
      setOpenConfigureNotificationModal={setOpenConfigureNotificationModal}
      channels={channels}
      containerClass={CALL_MENU_CONTAINER_CLASS}
      addChannelOption={addChannelOption}
    />
  );
};

const FrontNotificationIVRWrapper = ({ item, route, setRoute, channels, setOpenConfigureNotificationModal, isOffHours = false, routeModals, optionKey }) => {
  const notificationLogic = useNotificationAction("front", "ivrMenu", route, setRoute, item?.id ?? null, isOffHours, optionKey);
  const addChannelOption = routeModals
    ? { label: "Add Front Channel", onClick: () => routeModals.openAddChannelModal("front", notificationLogic.handleChannelChange) }
    : undefined;
  return (
    <FrontNotificationUI
      {...notificationLogic}
      setOpenConfigureNotificationModal={setOpenConfigureNotificationModal}
      channels={channels}
      containerClass={CALL_MENU_CONTAINER_CLASS}
      addChannelOption={addChannelOption}
    />
  );
};

const MENU_DIGIT_OPTIONS = IVR_MENU_DIGIT_ORDER;

function getOptionDigit(item, index, optionKey) {
  return resolveOptionDigit(item, optionKey, index);
}

const DEFAULT_BUSINESS_HOURS_CALENDAR = {
  mon: { active: true, earlyTime: "09:00", lateTime: "17:00" },
  tue: { active: true, earlyTime: "09:00", lateTime: "17:00" },
  wed: { active: true, earlyTime: "09:00", lateTime: "17:00" },
  thur: { active: true, earlyTime: "09:00", lateTime: "17:00" },
  fri: { active: true, earlyTime: "09:00", lateTime: "17:00" },
  sat: { active: false },
  sun: { active: false },
};

const IvrMenuOption = ({ item, index, route, setRoute, optionKey, enableBusinessHours = false }) => {
  const option = optionKey != null ? route?.ivrMenu?.options?.[optionKey] : item;
  const optionHasBusinessHours = option?.businessHoursEnabled === true || option?.primaryAction === "ivr";
  const days = [
    { key: "mon", label: "Mon" },
    { key: "tue", label: "Tue" },
    { key: "wed", label: "Wed" },
    { key: "thur", label: "Thu" },
    { key: "fri", label: "Fri" },
    { key: "sat", label: "Sat" },
    { key: "sun", label: "Sun" },
  ];

  const [openConfigureCallActionModal, setOpenConfigureCallActionModal] =
    useState(false);

  const [
    openConfigureCallActionForBusinessHoursModal,
    setOpenConfigureCallActionForBusinessHoursModal,
  ] = useState(false);

  const [
    openConfigureCallActionForAfterHoursModal,
    setOpenConfigureCallActionForAfterHoursModal,
  ] = useState(false);

  const [
    openConfigureNotificationForAfterHoursModal,
    setOpenConfigureNotificationForAfterHoursModal,
  ] = useState(false);

  const [
    openConfigureNotificationForBusinessHoursModal,
    setOpenConfigureNotificationForBusinessHoursModal,
  ] = useState(false);

  const [
    openConfigureNotificationMainModal,
    setOpenConfigureNotificationMainModal,
  ] = useState(false);

  const routeModals = useRouteConfigModals();
  const [localRecordings, setLocalRecordings] = useState(
    () => JSON.parse(localStorage.getItem("organization"))?.recordings?.L || []
  );
  const [localChannels, setLocalChannels] = useState(
    () => JSON.parse(localStorage.getItem("organization"))?.channels?.L || []
  );
  const recordings = routeModals?.recordings ?? localRecordings;
  const channels = routeModals?.channels ?? localChannels;

  const [editingDigit, setEditingDigit] = useState(false);
  const displayDigit = getOptionDigit(item, index, optionKey);
  const displayName = item?.name || `Press ${displayDigit}`;

  const handleDigitChange = (newDigit) => {
    if (newDigit === optionKey) {
      setEditingDigit(false);
      return;
    }
    setRoute((prev) => {
      const opts = { ...prev.ivrMenu.options };
      const opt = opts[optionKey];
      delete opts[optionKey];
      opts[newDigit] = {
        ...opt,
        digit: newDigit,
        id: `press_${newDigit}`,
        name: `Press ${newDigit}`,
      };
      return {
        ...prev,
        ivrMenu: { ...prev.ivrMenu, options: opts },
      };
    });
    setEditingDigit(false);
  };

  return (
    <div className="border rounded-lg p-4 bg-white relative ml-8">
      {/* Branch connector line */}
      <div className="absolute -left-8 top-0 w-8 h-6 border-l-2 border-b-2 border-gray-300 rounded-bl-lg"></div>
      
      <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-600">
        <Close
          fontSize="small"
          onClick={() => {
            setRoute((prev) => {
              const opts = { ...prev.ivrMenu?.options };
              delete opts[optionKey];
              return {
                ...prev,
                ivrMenu: {
                  ...prev.ivrMenu,
                    options: opts,
                },
              };
            });
          }}
        />
      </button>

      <div className="flex items-center space-x-2 mb-4">
        {editingDigit ? (
          <select
            autoFocus
            value={displayDigit}
            onChange={(e) => handleDigitChange(e.target.value)}
            onBlur={() => setEditingDigit(false)}
            className="w-8 min-w-[2rem] h-6 rounded-full bg-purple-100 text-purple-600 text-xs font-medium flex items-center justify-center border border-purple-300 focus:outline-none focus:ring-1 focus:ring-purple-500 cursor-pointer"
          >
            {MENU_DIGIT_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        ) : (
          <button
            type="button"
            onClick={() => setEditingDigit(true)}
            className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 text-xs font-medium flex items-center justify-center hover:bg-purple-200 transition-colors cursor-pointer"
            title="Click to change key"
          >
            {displayDigit}
          </button>
        )}
        <span className="text-sm font-semibold text-gray-900">{displayName}</span>
      </div>

      {/* Call Action */}
      <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 mb-2">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-sm font-bold text-gray-700">Call Action</div>
            {!optionHasBusinessHours && (!option?.primaryAction || option?.primaryAction === "ivr") && (
              <div className="text-sm text-gray-500">No action configured</div>
            )}
          </div>

          <button
            className="text-sm font-medium text-blue-600 hover:underline"
            onClick={() => {
              setOpenConfigureCallActionModal(true);
            }}
          >
            {optionHasBusinessHours
              ? "Change"
              : (!option?.primaryAction || option?.primaryAction === "ivr")
              ? "Add"
              : "Change"}
          </button>

          <ConfigureCallActionModal
            open={openConfigureCallActionModal}
            onClose={setOpenConfigureCallActionModal}
            route={route}
            enableBusinessHours={enableBusinessHours}
            callAction={(newAction) =>
              setRoute((prev) => {
                const current = prev.ivrMenu.options[optionKey];
                return {
                ...prev,
                ivrMenu: {
                  ...prev.ivrMenu,
                  options: {
                    ...prev.ivrMenu.options,
                    [optionKey]: {
                      ...current,
                      businessHoursEnabled: newAction === "ivr" ? true : false,
                      primaryAction: newAction === "ivr" ? null : newAction,
                      primaryNotification: null,
                      primaryChannel: null,
                      offHoursNotification: null,
                      offHoursChannel: null,
                      calendar: newAction === "ivr" ? DEFAULT_BUSINESS_HOURS_CALENDAR : null,
                      primaryRecording: (newAction === "send_to_vm" || newAction === "play_recording" || newAction === "ai_agent") ? null : current.primaryRecording,
                      offHoursAction: null,
                      ...numberFieldsForActionChange(
                        newAction === "ivr" ? null : newAction,
                        { currentNumber: current?.primaryNumber }
                      ),
                    },
                  },
                },
              };
              })
            }
          />
        </div>

        {!enableBusinessHours && optionHasBusinessHours && (
          <div className=" py-5  mt-4 w-full px-2 ">
            <div className="flex items-center space-x-1">
              <AccessTime
                className="text-orange-500 mt-0.5"
                sx={{
                  fontSize: "18px",
                  position: "relative",
                  bottom: "1px",
                }}
              />
              <div>
                <h2 className="text-sm text-gray-900">Business Hours</h2>
              </div>
            </div>
            <p className="text-[13px] mt-2 text-gray-600">
              Route differently during call hours
            </p>

            <Divider />
            <div className="text-[14px] font-medium text-gray-800 mb-4 mt-2">
              Weekly Schedule
            </div>

            {/* Days List */}
            <div className="divide-y divide-gray-300 border border-gray-100 rounded-xl  overflow-scroll lg:overflow-hidden">
              <div className="w-[130vw] lg:w-full">
                {days.map(({ key, label }) => {
                  const isActive = item?.calendar?.[key]?.active || false;
                  const startTime =
                    (item?.calendar?.[key]?.earlyTime ?? item?.calendar?.[key]?.early_time) || "09:00";
                  const endTime = (item?.calendar?.[key]?.lateTime ?? item?.calendar?.[key]?.late_time) || "17:00";

                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between px-4 py-3"
                    >
                      {/* Day Label */}
                      <div className="w-[60px] text-gray-800 text-sm">
                        {label}
                      </div>

                      {/* Status Badge */}
                      <div>
                        {isActive ? (
                          <span
                            className="px-2 py-0.5 rounded-md bg-green-50 text-green-700 text-xs border border-green-200 cursor-pointer"
                            onClick={() =>
                              setRoute((prev) => {
                                const updated = { ...prev };
                                updated.ivrMenu.options =
                                  mapIvrOptions(updated.ivrMenu.options, (opt, k) =>
                                    k === String(optionKey)
                                      ? {
                                          ...opt,
                                          calendar: {
                                            ...opt.calendar,
                                            [key]: {
                                              ...opt.calendar?.[key],
                                              active: false,
                                            },
                                          },
                                        }
                                      : opt
                                  );
                                return updated;
                              })
                            }
                          >
                            Open
                          </span>
                        ) : (
                          <span
                            className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-xs border border-gray-200 cursor-pointer"
                            onClick={() => {
                              setRoute((prev) => {
                                const updated = { ...prev };
                                updated.ivrMenu.options =
                                  mapIvrOptions(updated.ivrMenu?.options, (opt, k) =>
                                    k === String(optionKey)
                                      ? {
                                          ...opt,
                                          calendar: {
                                            ...opt.calendar,
                                            [key]: {
                                              ...opt.calendar?.[key],
                                              active: true,
                                              earlyTime: "09:00",
                                              lateTime: "17:00",
                                            },
                                          },
                                        }
                                      : opt
                                  );
                                return updated;
                              });
                            }}
                          >
                            Closed
                          </span>
                        )}
                      </div>

                      {/* Start Time */}
                      <div className="flex items-center gap-1 w-[120px]">
                        <AccessTime sx={{ fontSize: 18, color: "#6B7280" }} />
                        <TimePicker
                          className="custom-time-picker"
                          disableClock
                          clearIcon={null}
                          onChange={(value) =>
                            setRoute((prev) => {
                              const updated = { ...prev };
                              updated.ivrMenu.options =
                                mapIvrOptions(updated.ivrMenu.options, (opt, k) =>
                                  k === String(optionKey)
                                    ? {
                                        ...opt,
                                        calendar: {
                                          ...opt.calendar,
                                            [key]: {
                                              ...opt.calendar?.[key],
                                              earlyTime: value,
                                            },
                                        },
                                      }
                                    : opt
                                );
                              return updated;
                            })
                          }
                          value={startTime}
                          disabled={!isActive}
                        />
                      </div>

                      <div className="text-sm text-gray-500">to</div>

                      {/* End Time */}
                      <div className="flex items-center gap-1 w-[120px]">
                        <TimePicker
                          className="custom-time-picker"
                          disableClock
                          clearIcon={null}
                          onChange={(value) =>
                            setRoute((prev) => {
                              const updated = { ...prev };
                              updated.ivrMenu.options =
                                mapIvrOptions(updated.ivrMenu.options, (opt, k) =>
                                  k === String(optionKey)
                                    ? {
                                        ...opt,
                                        calendar: {
                                          ...opt.calendar,
                                            [key]: {
                                              ...opt.calendar?.[key],
                                              lateTime: value,
                                            },
                                        },
                                      }
                                    : opt
                                );
                              return updated;
                            })
                          }
                          value={endTime}
                          disabled={!isActive}
                        />
                        <AccessTime sx={{ fontSize: 18, color: "#6B7280" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick Apply Buttons */}
            <span className="text-gray-600 text-[14px] mt-2">Quick apply:</span>
            <div className="mt-4 flex flex-wrap lg:flex-nowrap items-center gap-2 text-sm">
              {/* Mon–Fri 9:00–17:00 */}
              <button
                className="text-[13px] px-2 py-1 border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={() =>
                  setRoute((prev) => {
                    const updated = { ...prev };
                    updated.ivrMenu.options = mapIvrOptions(updated.ivrMenu.options, (opt, k) =>
                        k === String(optionKey)
                          ? {
                              ...opt,
                              calendar: {
                                ...opt.calendar,
                                mon: {
                                  active: true,
                                  earlyTime: "09:00",
                                  lateTime: "17:00",
                                },
                                tue: {
                                  active: true,
                                  earlyTime: "09:00",
                                  lateTime: "17:00",
                                },
                                wed: {
                                  active: true,
                                  earlyTime: "09:00",
                                  lateTime: "17:00",
                                },
                                thur: {
                                  active: true,
                                  earlyTime: "09:00",
                                  lateTime: "17:00",
                                },
                                fri: {
                                  active: true,
                                  earlyTime: "09:00",
                                  lateTime: "17:00",
                                },
                                sat: { active: false },
                                sun: { active: false },
                              },
                            }
                          : opt
                    );
                    return updated;
                  })
                }
              >
                Mon–Fri 9:00–17:00
              </button>

              {/* Weekend Closed */}
              <button
                className="px-2 text-[13px] py-1 border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={() =>
                  setRoute((prev) => {
                    const updated = { ...prev };
                    updated.ivrMenu.options = mapIvrOptions(updated.ivrMenu.options, (opt, k) =>
                        k === String(optionKey)
                          ? {
                              ...opt,
                              calendar: {
                                ...opt.calendar,
                                sat: { active: false },
                                sun: { active: false },
                              },
                            }
                          : opt
                    );
                    return updated;
                  })
                }
              >
                Weekend Closed
              </button>

              {/* Clear All (set all days active 9–17) */}
              <button
                className="px-2 py-1 text-[13px] border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={() =>
                  setRoute((prev) => {
                    const updated = { ...prev };
                    updated.ivrMenu.options = mapIvrOptions(updated.ivrMenu.options, (opt, k) =>
                        k === String(optionKey)
                          ? {
                              ...opt,
                              calendar: {
                                mon: {
                                  active: false,
                                  earlyTime: "09:00",
                                  lateTime: "17:00",
                                },
                                tue: {
                                  active: false,
                                  earlyTime: "09:00",
                                  lateTime: "17:00",
                                },
                                wed: {
                                  active: false,
                                  earlyTime: "09:00",
                                  lateTime: "17:00",
                                },
                                thur: {
                                  active: false,
                                  earlyTime: "09:00",
                                  lateTime: "17:00",
                                },
                                fri: {
                                  active: false,
                                  earlyTime: "09:00",
                                  lateTime: "17:00",
                                },
                                sat: {
                                  active: false,
                                  earlyTime: "09:00",
                                  lateTime: "17:00",
                                },
                                sun: {
                                  active: false,
                                  earlyTime: "09:00",
                                  lateTime: "17:00",
                                },
                              },
                            }
                          : opt
                    );
                    return updated;
                  })
                }
              >
                Clear All
              </button>
            </div>

            {/* Timezone Info */}
            <div className="mt-4 flex justify-end">
              <div className="text-sm text-gray-500 border border-gray-200 rounded-md px-3 py-1 bg-gray-50">
                <span className="text-gray-400">Timezone</span>{" "}
                {
                  JSON.parse(localStorage.getItem("organization")).org_timezone
                    .S
                }
              </div>
            </div>

            <div className="flex flex-wrap lg:flex-nowrap  items-start lg:space-x-4 w-full mt-4">
              <div className="w-full">
                <div className="text-[13px] font-semibold text-gray-500">
                  DURING BUSINESS HOURS 
                </div>
                <div className="bg-white rounded-lg border border-gray-200 py-4 px-2 shadow-sm w-full">
                  {(!option?.primaryAction || option?.primaryAction === "ivr") && (
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-lg cursor-pointer 
flex items-center justify-center h-16 hover:border-gray-400 transition text-[13px]"
                      onClick={() =>
                        setOpenConfigureCallActionForBusinessHoursModal(true)
                      }
                    >
                      <Add className="text-gray-500  mr-2" />
                      <span className="text-gray-600">Add Call Action</span>
                    </div>
                  )}

                  {isTwilanswerAppAction(option?.primaryAction) && (
                    <TwilanswerAppIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      recordings={recordings}
                      setOpenConfigureSlotModal={setOpenConfigureCallActionForBusinessHoursModal}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  {isForwardFamilyAction(option?.primaryAction) && (
                    <ForwardIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      channels={channels}
                      recordings={recordings}
                      setOpenConfigureSlotModal={setOpenConfigureCallActionForBusinessHoursModal}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  {/* {option?.primaryAction == "ring_browser" && (
                  <div className="">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center  gap-1">
                        <HeadsetMic className="text-green-500" />
                        <h2 className="font-medium text-[13px] text-gray-800">
                          Ring User or Group in Browser
                        </h2>
                      </div>
                      <button
                        className=" text-blue-600 font-medium hover:underline text-[13px]"
                        onClick={() =>
                          setOpenConfigureCallActionForBusinessHoursModal(
                            true
                          )
                        }
                      >
                        Change
                      </button>
                    </div>

                    <hr className="border-gray-200 mb-4" />

                    <div className="text-[13px] text-gray-400 italic">
                      Configuration options for "Ring User or Group in
                      Browser" will be available here.
                    </div>
                  </div>
                )} */}

                  {option?.primaryAction == "send_to_vm" && (
                    <SendToVoicemailIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      recordings={recordings}
                      setOpenConfigureCallActionModal={setOpenConfigureCallActionForBusinessHoursModal}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  {option?.primaryAction == "play_recording" && (
                    <PlayRecordingIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      recordings={recordings}
                      setOpenConfigureCallActionModal={setOpenConfigureCallActionForBusinessHoursModal}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  {option?.primaryAction == "ai_agent" && (
                    <ElevenLabsIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      channels={channels}
                      setOpenConfigureCallActionModal={setOpenConfigureCallActionForBusinessHoursModal}
                      optionKey={optionKey}
                    />
                  )}

                  <ConfigureCallActionForBusinessModal
                    open={openConfigureCallActionForBusinessHoursModal}
                    onClose={setOpenConfigureCallActionForBusinessHoursModal}
                    route={route}
                    callAction={(newAction) =>
                      setRoute((prev) => {
                        const current = prev.ivrMenu.options[optionKey];
                        return {
                        ...prev,
                        ivrMenu: {
                          ...prev.ivrMenu,
                          options: {
                            ...prev.ivrMenu.options,
                            [optionKey]: {
                              ...current,
                              primaryAction: newAction,
                              primaryRecording: (newAction === "send_to_vm" || newAction === "play_recording" || newAction === "ai_agent") ? null : current.primaryRecording,
                              callApproval: null,
                              callerIdPref: null,
                              ...numberFieldsForActionChange(newAction, {
                                currentNumber: current?.primaryNumber,
                              }),
                            },
                          },
                        },
                      };
                      })
                    }
                  />
                </div>
              </div>

              <div className="w-full">
                <div className="text-[13px] font-semibold text-gray-500">
                  AFTER HOURS 
                </div>
                <div className="bg-white rounded-lg border border-gray-200 py-4 px-2 shadow-sm w-full">
                  {!option?.offHoursAction && (
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-lg cursor-pointer 
flex items-center justify-center h-16 hover:border-gray-400 transition text-[13px]"
                      onClick={() =>
                        setOpenConfigureCallActionForAfterHoursModal(true)
                      }
                    >
                      <Add className="text-gray-500  mr-2" />
                      <span className="text-gray-600">Add Call Action</span>
                    </div>
                  )}

                  {isTwilanswerAppAction(option?.offHoursAction) && (
                    <TwilanswerAppIVRWrapperOffHours
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      recordings={recordings}
                      setOpenConfigureSlotModal={setOpenConfigureCallActionForAfterHoursModal}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  {isForwardFamilyAction(option?.offHoursAction) && (
                    <ForwardIVRWrapperOffHours
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      channels={channels}
                      recordings={recordings}
                      setOpenConfigureSlotModal={setOpenConfigureCallActionForAfterHoursModal}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  {/* {option?.offHoursAction == "ring_browser" && (
                  <div className="">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center  gap-1">
                        <HeadsetMic className="text-green-500" />
                        <h2 className="font-medium text-[13px] text-gray-800">
                          Ring User or Group in Browser
                        </h2>
                      </div>
                      <button
                        className=" text-blue-600 font-medium hover:underline text-[13px]"
                        onClick={() =>
                          setOpenConfigureCallActionForAfterHoursModal(
                            true
                          )
                        }
                      >
                        Change
                      </button>
                    </div>

                    <hr className="border-gray-200 mb-4" />

                    <div className="text-[13px] text-gray-400 italic">
                      Configuration options for "Ring User or Group in
                      Browser" will be available here.
                    </div>
                  </div>
                )} */}

                  {option
                    .offHoursAction == "send_to_vm" && (
                    <SendToVoicemailIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      recordings={recordings}
                      setOpenConfigureCallActionModal={setOpenConfigureCallActionForAfterHoursModal}
                      isOffHours={true}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  {option?.offHoursAction == "play_recording" && (
                    <PlayRecordingIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      recordings={recordings}
                      setOpenConfigureCallActionModal={setOpenConfigureCallActionForAfterHoursModal}
                      isOffHours={true}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  {option?.offHoursAction == "ai_agent" && (
                    <ElevenLabsIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      channels={channels}
                      setOpenConfigureCallActionModal={setOpenConfigureCallActionForAfterHoursModal}
                      isOffHours={true}
                      optionKey={optionKey}
                    />
                  )}

                  <ConfigureCallActionForAfterModal
                    open={openConfigureCallActionForAfterHoursModal}
                    onClose={setOpenConfigureCallActionForAfterHoursModal}
                    route={route}
                    callAction={(newAction) =>
                      setRoute((prev) => {
                        const current = prev.ivrMenu.options[optionKey];
                        return {
                        ...prev,
                        ivrMenu: {
                          ...prev.ivrMenu,
                          options: {
                            ...prev.ivrMenu.options,
                            [optionKey]: {
                              ...current,
                              offHoursAction: newAction,
                              offHoursChannel: null,
                              offHoursRecording: null,
                              offHoursCallAprroval: null,
                              offHoursCallerIdPref: null,
                              ...numberFieldsForActionChange(newAction, {
                                currentNumber: current?.offHoursNumber,
                                numberKey: "offHoursNumber",
                              }),
                            },
                          },
                        },
                      };
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <div className="text-[14px] font-bold mt-4"> Notifications </div>
            <div className="flex flex-wrap lg:flex-nowrap items-start lg:space-x-4 gap-2 w-full mt-4">
              <div className="w-full">
                <div className="text-[13px] font-semibold text-gray-500">
                  DURING BUSINESS HOURS
                </div>
                <div className="bg-white rounded-lg border border-gray-200 py-4 px-2 shadow-sm w-full">
                  {!option?.primaryNotification && (
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-lg cursor-pointer 
flex items-center justify-center h-16 hover:border-gray-400 transition text-[13px]"
                      onClick={() =>
                        setOpenConfigureNotificationForBusinessHoursModal(true)
                      }
                    >
                      <Add className="text-gray-500  mr-2" />
                      <span className="text-gray-600">Add Notification</span>
                    </div>
                  )}

                  {option
                    .primaryNotification == "email" && (
                    <EmailNotificationIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      channels={channels}
                      setOpenConfigureNotificationModal={setOpenConfigureNotificationForBusinessHoursModal}
                      isOffHours={false}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  {option
                    .primaryNotification == "slack" && (
                    <SlackNotificationIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      channels={channels}
                      setOpenConfigureNotificationModal={setOpenConfigureNotificationForBusinessHoursModal}
                      isOffHours={false}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  {option
                    .primaryNotification == "sms" && (
                    <SmsNotificationIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      channels={channels}
                      setOpenConfigureNotificationModal={setOpenConfigureNotificationForBusinessHoursModal}
                      isOffHours={false}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  {option
                    .primaryNotification == "front" && (
                    <FrontNotificationIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      channels={channels}
                      setOpenConfigureNotificationModal={setOpenConfigureNotificationForBusinessHoursModal}
                      isOffHours={false}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  <ConfigureNotificationForBusinessHours
                    open={openConfigureNotificationForBusinessHoursModal}
                    onClose={setOpenConfigureNotificationForBusinessHoursModal}
                    callAction={(newAction) =>
                      setRoute((prev) => ({
                        ...prev,
                        ivrMenu: {
                          ...prev.ivrMenu,
                          options: {
                            ...prev.ivrMenu.options,
                            [optionKey]: {
                              ...prev.ivrMenu.options[optionKey],
                              primaryNotification: newAction,
                              primaryChannel: null,
                            },
                          },
                        },
                      }))
                    }
                  />
                </div>
              </div>

              <div className="w-full">
                <div className="text-[13px] font-semibold text-gray-500">
                  AFTER HOURS 
                </div>
                <div className="bg-white rounded-lg border border-gray-200 py-4 px-2 shadow-sm w-full">
                  {!option?.offHoursNotification && (
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-lg cursor-pointer 
flex items-center justify-center h-16 hover:border-gray-400 transition text-[13px]"
                      onClick={() =>
                        setOpenConfigureNotificationForAfterHoursModal(true)
                      }
                    >
                      <Add className="text-gray-500  mr-2" />
                      <span className="text-gray-600">Add Notification</span>
                    </div>
                  )}

                  {option
                    .offHoursNotification == "email" && (
                    <EmailNotificationIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      channels={channels}
                      setOpenConfigureNotificationModal={setOpenConfigureNotificationForAfterHoursModal}
                      isOffHours={true}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  {option
                    .offHoursNotification == "slack" && (
                    <SlackNotificationIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      channels={channels}
                      setOpenConfigureNotificationModal={setOpenConfigureNotificationForAfterHoursModal}
                      isOffHours={true}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  {option
                    .offHoursNotification == "sms" && (
                    <SmsNotificationIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      channels={channels}
                      setOpenConfigureNotificationModal={setOpenConfigureNotificationForAfterHoursModal}
                      isOffHours={true}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  {option
                    .offHoursNotification == "front" && (
                    <FrontNotificationIVRWrapper
                      item={item}
                      route={route}
                      setRoute={setRoute}
                      channels={channels}
                      setOpenConfigureNotificationModal={setOpenConfigureNotificationForAfterHoursModal}
                      isOffHours={true}
                      routeModals={routeModals}
                      optionKey={optionKey}
                    />
                  )}

                  <ConfigureNotificationForAfterHours
                    open={openConfigureNotificationForAfterHoursModal}
                    onClose={setOpenConfigureNotificationForAfterHoursModal}
                    callAction={(newAction) =>
                      setRoute((prev) => ({
                        ...prev,
                        ivrMenu: {
                          ...prev.ivrMenu,
                          options: {
                            ...prev.ivrMenu.options,
                            [optionKey]: {
                              ...prev.ivrMenu.options[optionKey],
                              offHoursNotification: newAction,
                              offHoursChannel: null,
                            },
                          },
                        },
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {!optionHasBusinessHours && isTwilanswerAppAction(option?.primaryAction) && (
          <TwilanswerAppIVRWrapper item={item} route={route} setRoute={setRoute} recordings={recordings} routeModals={routeModals} optionKey={optionKey} />
        )}

        {!optionHasBusinessHours && isForwardFamilyAction(option?.primaryAction) && (
          <ForwardIVRWrapper item={item} route={route} setRoute={setRoute} channels={channels} recordings={recordings} routeModals={routeModals} optionKey={optionKey} />
        )}

        {/* {option
        ?.primaryAction == "ring_browser" && (
        <div className=" space-y-4 shadow-sm bg-gray-50  mt-2 rounded-lg ">
        
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center ">
              <HeadsetMic className="text-green-500 mr-2" />
              <div>
                <h2 className="font-semibold text-[15px]  text-gray-800">
                  Ring User or Group in Browser
                </h2>

                <div className="text-[13px]">
                  Ring a configured user or group
                </div>
              </div>
            </div>
          </div>
          <Divider />

         
          <div className="mb-4">
            <div className="flex items-center gap-1 mb-1">
              <span className="text-sm font-medium text-gray-700">
                Ring target
              </span>
            </div>
            <select
              className="w-full bg-transparent border-b-[1px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              defaultValue=""
              onChange={(e) => {
                setRoute((prev) => ({
                  ...prev,
                  ivrMenu: {
                    ...prev.ivrMenu,
                    options: {
                      ...prev.ivrMenu.options,
                      [optionKey]: {
                        ...prev.ivrMenu.options[optionKey],
                        ringTarget: e.target.value,
                        primaryAction: "ring_browser",
                      },
                    },
                  },
                }));
              }}
            >
              <option value="" disabled>
                Select a ring target
              </option>
              <option value="ring_group" disabled>
                Ring Group
              </option>
              <option value="ring_user">Ring User</option>
            </select>
          </div>

          {option?.ringTarget == "ring_group" && (
            <div className="mb-4">
              <div className="flex items-center gap-1 mb-1">
                <span className="text-sm font-medium text-gray-700">
                  Ring Group
                </span>
              </div>
              <select
                className="w-full bg-transparent border-b-[1px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                defaultValue=""
                onChange={(e) => {
                  setRoute((prev) => ({
                    ...prev,
                    ivrMenu: {
                      ...prev.ivrMenu,
                      options: {
                        ...prev.ivrMenu.options,
                        [optionKey]: {
                          ...prev.ivrMenu.options[optionKey],
                          ringTarget: "ring_group",
                          ringGroup: e.target.value,
                          ringUser: null,
                          primaryAction: "ring_browser",
                        },
                      },
                    },
                  }));
                }}
              >
                <option value="" disabled>
                  Select a ring group
                </option>
                <option value="ring_group1" disabled>
                  Ring Group1
                </option>
                <option value="ring_group2">Ring Group2</option>
              </select>
            </div>
          )}

          {option?.ringTarget == "ring_user" && (
            <div className="mb-4">
              <div className="flex items-center gap-1 mb-1">
                <span className="text-sm font-medium text-gray-700">
                  Ring Group
                </span>
              </div>
              <select
                className="w-full bg-transparent border-b-[1px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                defaultValue=""
                onChange={(e) => {
                  setRoute((prev) => ({
                    ...prev,
                    ivrMenu: {
                      ...prev.ivrMenu,
                      options: {
                        ...prev.ivrMenu.options,
                        [optionKey]: {
                          ...prev.ivrMenu.options[optionKey],
                          ringTarget: "ring_user",
                          ringUser: e.target.value,
                          ringGroup: null,
                          primaryAction: "ring_browser",
                        },
                      },
                    },
                  }));
                }}
              >
                <option value="" disabled>
                  Select a ring group
                </option>
                <option value="ring_user1" disabled>
                  Ring user1
                </option>
                <option value="ring_user2">Ring user2</option>
              </select>
            </div>
          )}
        </div>
      )} */}

        {!optionHasBusinessHours && option?.primaryAction == "send_to_vm" && (
          <SendToVoicemailIVRWrapper 
            item={item} 
            route={route} 
            setRoute={setRoute} 
            recordings={recordings} 
            setOpenConfigureCallActionModal={null}
            routeModals={routeModals}
            optionKey={optionKey}
          />
        )}

        {!optionHasBusinessHours && option?.primaryAction == "play_recording" && (
          <PlayRecordingIVRWrapper 
            item={item} 
            route={route} 
            setRoute={setRoute} 
            recordings={recordings} 
            setOpenConfigureCallActionModal={null}
            routeModals={routeModals}
            optionKey={optionKey}
          />
        )}

        {!optionHasBusinessHours && option?.primaryAction == "ai_agent" && (
          <ElevenLabsIVRWrapper 
            item={item} 
            route={route} 
            setRoute={setRoute} 
            channels={channels}
            setOpenConfigureCallActionModal={null}
            optionKey={optionKey}
          />
        )}
      </div>

      {/* Notification */}

      <ConfigureNotificationModal
        open={openConfigureNotificationMainModal}
        onClose={setOpenConfigureNotificationMainModal}
        slot={(newAction) =>
          setRoute((prev) => ({
            ...prev,
            ivrMenu: {
              ...prev.ivrMenu,
              options: {
                ...prev.ivrMenu.options,
                [optionKey]: {
                  ...prev.ivrMenu.options[optionKey],
                  primaryNotification: newAction,
                  primaryChannel: null,
                },
              },
            },
          }))
        }
      />

      {!optionHasBusinessHours && !option?.primaryNotification && (
          <div className="flex justify-between items-center bg-gray-50 border border-gray-100 rounded-lg p-3 mb-2">
            <div>
              <div className="text-sm font-bold text-gray-700">
                Notification
              </div>
              {!item.primaryNotification && (
                <div className="text-sm text-gray-500">
                  No Notification Configured
                </div>
              )}
            </div>

            <button
              className="text-sm font-medium text-blue-600 hover:underline"
              onClick={() => setOpenConfigureNotificationMainModal(true)}
            >
              {!option
                .primaryNotification
                ? "Add"
                : "change"}
            </button>
          </div>
        )}
      {!optionHasBusinessHours && option?.primaryNotification == "email" && (
          <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 mb-2">
            <EmailNotificationIVRWrapper
              item={item}
              route={route}
              setRoute={setRoute}
              channels={channels}
              setOpenConfigureNotificationModal={setOpenConfigureNotificationMainModal}
              isOffHours={false}
              routeModals={routeModals}
              optionKey={optionKey}
            />
          </div>
        )}

      {!optionHasBusinessHours && option?.primaryNotification == "slack" && (
          <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 mb-2">
            <SlackNotificationIVRWrapper
              item={item}
              route={route}
              setRoute={setRoute}
              channels={channels}
              setOpenConfigureNotificationModal={setOpenConfigureNotificationMainModal}
              isOffHours={false}
              routeModals={routeModals}
              optionKey={optionKey}
            />
          </div>
        )}

      {!optionHasBusinessHours && option?.primaryNotification == "sms" && (
          <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 mb-2">
            <SmsNotificationIVRWrapper
              item={item}
              route={route}
              setRoute={setRoute}
              channels={channels}
              setOpenConfigureNotificationModal={setOpenConfigureNotificationMainModal}
              isOffHours={false}
              routeModals={routeModals}
              optionKey={optionKey}
            />
          </div>
        )}

      {!optionHasBusinessHours && option?.primaryNotification == "front" && (
          <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 mb-2">
            <FrontNotificationIVRWrapper
              item={item}
              route={route}
              setRoute={setRoute}
              channels={channels}
              setOpenConfigureNotificationModal={setOpenConfigureNotificationMainModal}
              isOffHours={false}
              routeModals={routeModals}
              optionKey={optionKey}
            />
          </div>
        )}
    </div>
  );
};

export default IvrMenuOption;
